import { ValidationSchemas, lookupConfig } from "./formConstants";
import CopyFieldsButton from "../components/CopyFieldsButton";

export const PURPOSE_OF_WELL_FIELDS = [
  "fl_domestic",
  "fl_agricultural",
  "fl_geothermal_exempt",
  "fl_wildlife",
  "fl_irrigation",
  "fl_pws",
  "fl_community_ws",
  "fl_retail_pws",
  "fl_commercial",
  "fl_dewatering",
  "fl_industrial",
  "fl_injection",
  "fl_monitor",
  "fl_aquifer_test_obser",
  "fl_aquifer_test_pump",
  "fl_remediation",
  "fl_other_exempt",
];

export const newWellRegistrationFormConfig = [
  {
    title: "Well Owner Information",
    subtitle:
      "Any person who has the right to produce groundwater, either by ownership, contract, lease, easement, or any other estate in the land.",
    step: "Well Owner Info",
    fields: [
      {
        name: "name_company_owner",
        label: "Business/Organization (if applicable)",
        type: "text",
        validation: ValidationSchemas.string,
      },
      {
        name: "name_first_owner",
        label: "First Name",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "name_last_owner",
        label: "Last Name",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_line_1_owner",
        label: "Mailing Address",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_city_owner",
        label: "City",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_state_owner",
        label: "State",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_zip_owner",
        label: "Zip",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "phone_primary_owner",
        label: "Phone",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "phone_alternate_owner",
        label: "Alternate Phone",
        type: "text",
        validation: ValidationSchemas.string,
      },
      {
        name: "phone_fax_owner",
        label: "Fax",
        type: "text",
        validation: ValidationSchemas.string,
      },
      {
        name: "email_owner",
        label: "Email",
        type: "text",
        validation: ValidationSchemas.requiredEmail,
        infoTooltip:
          "Only used for confirmation and HTGCD communication about well",
      },
      {
        name: "real_estate_txn",
        label: "Part of near-term real estate transaction?",
        type: "radioBoolean",
        validation: ValidationSchemas.requiredBoolean,
        infoTooltip:
          "If yes, new owner must register ownership change within 60 days of transaction",
      },
      {
        name: "real_estate_txn_number",
        label: "Real Estate Transaction Number",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) => values.real_estate_txn,
          deps: ["real_estate_txn"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (data.real_estate_txn && !data.real_estate_txn_number) {
            return "Required if Real Estate Transaction is selected";
          }
          return undefined;
        },
      },
    ],
  },
  {
    title: "Purpose of Well",
    subtitle: "Check One",
    step: "Purpose of Well",
    fields: [
      {
        name: "exempt_purpose_types",
        type: "subHeader",
        label: "Exempt Purpose Types",
        content: (
          <>
            See{" "}
            <a
              href="https://haysgroundwater.com/regulatory/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Hays Trinity Groundwater Conservation District Rules
            </a>{" "}
            for full definitions.
          </>
        ),
      },
      {
        name: "fl_domestic",
        label: "Residential / Domestic Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A well used solely for domestic use by a single private residential household and producing less than 25,000 gallons per day. The private use of groundwater by an individual or a household, not provided by a third party, for the daily needs of a household, including water for use inside the home; for irrigation of lawns, or of a family garden or orchard that produces fruits or vegetables for consumption within the residence and not for sale; for watering of domestic animals; for swimming pools associated with a single residence; for stock-watering ponds of less than ¼-acre extent with an average depth of less than 3-feet, that are lined with bentonite, geomembrane, or an equivalent sealing material as adjudged by the District. Domestic use does not include water used by, or to support, activities for which consideration is given or received or for which the product of the activity is sold. Domestic use does not include use by or for a third party, including a public water system.",
          falseLabel:
            "A well used solely for domestic use by a single private residential household and producing less than 25,000 gallons per day. The private use of groundwater by an individual or a household, not provided by a third party, for the daily needs of a household, including water for use inside the home; for irrigation of lawns, or of a family garden or orchard that produces fruits or vegetables for consumption within the residence and not for sale; for watering of domestic animals; for swimming pools associated with a single residence; for stock-watering ponds of less than ¼-acre extent with an average depth of less than 3-feet, that are lined with bentonite, geomembrane, or an equivalent sealing material as adjudged by the District. Domestic use does not include water used by, or to support, activities for which consideration is given or received or for which the product of the activity is sold. Domestic use does not include use by or for a third party, including a public water system.",
          nullLabel:
            "A well used solely for domestic use by a single private residential household and producing less than 25,000 gallons per day. The private use of groundwater by an individual or a household, not provided by a third party, for the daily needs of a household, including water for use inside the home; for irrigation of lawns, or of a family garden or orchard that produces fruits or vegetables for consumption within the residence and not for sale; for watering of domestic animals; for swimming pools associated with a single residence; for stock-watering ponds of less than ¼-acre extent with an average depth of less than 3-feet, that are lined with bentonite, geomembrane, or an equivalent sealing material as adjudged by the District. Domestic use does not include water used by, or to support, activities for which consideration is given or received or for which the product of the activity is sold. Domestic use does not include use by or for a third party, including a public water system.",
        },
      },
      {
        name: "fl_agricultural",
        label: "Agricultural and Livestock Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "The use of groundwater for: (A) Cultivating the soil to produce crops for human food, animal feed, or planting seed or for the production of fibers; (B) Practicing floriculture, viticulture, silviculture, and horticulture, including the cultivation of plants in containers or non-soil media, by a nursery grower; (C) Raising, feeding, or keeping animals for breeding purposes or for the production of food or fiber, leather, pelts, or other tangible products having a commercial value; (D) Planting cover crops, including cover crops cultivated for transplantation, or leaving land idle for the purpose of participating in any governmental program or normal crop or livestock rotation procedure; (E) Engaging in wildlife management as defined in Texas Tax Code Section 23.51(7); and, (F) Raising or keeping equine animals.",
          falseLabel:
            "The use of groundwater for: (A) Cultivating the soil to produce crops for human food, animal feed, or planting seed or for the production of fibers; (B) Practicing floriculture, viticulture, silviculture, and horticulture, including the cultivation of plants in containers or non-soil media, by a nursery grower; (C) Raising, feeding, or keeping animals for breeding purposes or for the production of food or fiber, leather, pelts, or other tangible products having a commercial value; (D) Planting cover crops, including cover crops cultivated for transplantation, or leaving land idle for the purpose of participating in any governmental program or normal crop or livestock rotation procedure; (E) Engaging in wildlife management as defined in Texas Tax Code Section 23.51(7); and, (F) Raising or keeping equine animals.",
          nullLabel:
            "The use of groundwater for: (A) Cultivating the soil to produce crops for human food, animal feed, or planting seed or for the production of fibers; (B) Practicing floriculture, viticulture, silviculture, and horticulture, including the cultivation of plants in containers or non-soil media, by a nursery grower; (C) Raising, feeding, or keeping animals for breeding purposes or for the production of food or fiber, leather, pelts, or other tangible products having a commercial value; (D) Planting cover crops, including cover crops cultivated for transplantation, or leaving land idle for the purpose of participating in any governmental program or normal crop or livestock rotation procedure; (E) Engaging in wildlife management as defined in Texas Tax Code Section 23.51(7); and, (F) Raising or keeping equine animals.",
        },
      },
      {
        name: "num_ag_registration",
        label: "Texas Agricultural or Timber Registration Number",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) => values.fl_agricultural,
          deps: ["fl_agricultural"],
          required: true,
        },
        refine: (data) => {
          if (data.fl_agricultural && !data.num_ag_registration) {
            return "Required if Agricultural Well is selected";
          }
          return undefined;
        },
      },
      {
        name: "fl_geothermal_exempt",
        label: "GeoExchange / Geothermal Exempt",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A well used solely for earth-coupled heat exchange purposes and not equipped to produce groundwater.",
          falseLabel:
            "A well used solely for earth-coupled heat exchange purposes and not equipped to produce groundwater.",
          nullLabel:
            "A well used solely for earth-coupled heat exchange purposes and not equipped to produce groundwater.",
        },
      },
      {
        name: "num_geothermal_exempt",
        label: "Geothermal Exempt Registration Number",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) => values.fl_geothermal_exempt,
          deps: ["fl_geothermal_exempt"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (data.fl_geothermal_exempt && !data.num_geothermal_exempt) {
            return "Required if GeoExchange / Geothermal Exempt is selected";
          }
          return undefined;
        },
      },
      {
        name: "num_of_wells_geothermal_exempt",
        label: "Number of Wells for GeoExchange / Geothermal Exempt",
        type: "number",
        validation: ValidationSchemas.positiveInt,
        showIf: {
          fn: (values) => values.fl_geothermal_exempt,
          deps: ["fl_geothermal_exempt"],
          resetValue: null,
          required: true,
        },
        refine: (data) => {
          if (
            data.fl_geothermal_exempt &&
            !data.num_of_wells_geothermal_exempt
          ) {
            return "Required if GeoExchange / Geothermal Exempt is selected";
          }
          return undefined;
        },
      },
      {
        name: "fl_wildlife",
        label: "Wildlife Management Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "a well drilled and operated solely for providing water for wildlife, if the well is drilled, completed, or equipped so that it is incapable of producing more than 25,000 gallons of groundwater a day and located on a tract of land equal to or greater than 10 acres.",
          falseLabel:
            "a well drilled and operated solely for providing water for wildlife, if the well is drilled, completed, or equipped so that it is incapable of producing more than 25,000 gallons of groundwater a day and located on a tract of land equal to or greater than 10 acres.",
          nullLabel:
            "a well drilled and operated solely for providing water for wildlife, if the well is drilled, completed, or equipped so that it is incapable of producing more than 25,000 gallons of groundwater a day and located on a tract of land equal to or greater than 10 acres.",
        },
      },
      {
        name: "non_exempt_purpose_types",
        type: "subHeader",
        label: "Non-Exempt Purpose Types",
        content: (
          <>
            Requires Drilling Permit. Complete this Well Notification Form and
            then contact the District Office for a Drilling Permit Application.
            See{" "}
            <a
              href="https://haysgroundwater.com/regulatory/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Hays Trinity Groundwater Conservation District Rules
            </a>{" "}
            for additional information.
          </>
        ),
      },
      {
        name: "fl_irrigation",
        label: "Irrigation Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A well used to supply water for application to plants or land in order to promote growth of plants, turf, or trees. Agriculture and Residential/Domestic purposes are exempt from obtaining an operating permit (see definitions above).",
          falseLabel:
            "A well used to supply water for application to plants or land in order to promote growth of plants, turf, or trees. Agriculture and Residential/Domestic purposes are exempt from obtaining an operating permit (see definitions above).",
          nullLabel:
            "A well used to supply water for application to plants or land in order to promote growth of plants, turf, or trees. Agriculture and Residential/Domestic purposes are exempt from obtaining an operating permit (see definitions above).",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_pws",
        label: "Public Water System",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A system as defined by 30 Texas Administrative Code Section 290.38 for providing water to the public for human consumption.",
          falseLabel:
            "A system as defined by 30 Texas Administrative Code Section 290.38 for providing water to the public for human consumption.",
          nullLabel:
            "A system as defined by 30 Texas Administrative Code Section 290.38 for providing water to the public for human consumption.",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_community_ws",
        label: "Community Water System",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A system, other than a public water system, that supplies groundwater to long-term living units such as apartment complexes, town homes, or multiple dwellings on a shared well.",
          falseLabel:
            "A system, other than a public water system, that supplies groundwater to long-term living units such as apartment complexes, town homes, or multiple dwellings on a shared well.",
          nullLabel:
            "A system, other than a public water system, that supplies groundwater to long-term living units such as apartment complexes, town homes, or multiple dwellings on a shared well.",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_retail_pws",
        label: "Retail Public Water Utility",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A person providing groundwater to the public for human consumption through pipes or other constructed conveyances. As defined by 30 Texas Administrative Code Section 291.3, it is any person, corporation, public utility, water supply corporation, municipality, political subdivision or agency operating, maintaining, or controlling within the District facilities for providing potable water service for compensation.",
          falseLabel:
            "A person providing groundwater to the public for human consumption through pipes or other constructed conveyances. As defined by 30 Texas Administrative Code Section 291.3, it is any person, corporation, public utility, water supply corporation, municipality, political subdivision or agency operating, maintaining, or controlling within the District facilities for providing potable water service for compensation.",
          nullLabel:
            "A person providing groundwater to the public for human consumption through pipes or other constructed conveyances. As defined by 30 Texas Administrative Code Section 291.3, it is any person, corporation, public utility, water supply corporation, municipality, political subdivision or agency operating, maintaining, or controlling within the District facilities for providing potable water service for compensation.",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_commercial",
        label: "Commercial Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "The use of groundwater to supply water to properties or establishments that are in business to build, supply or sell products, or provide goods, services or repairs and that use water in those processes, or to supply water to the business establishment primarily for employee and customer conveniences (i.e. flushing of toilets, sanitary purposes, or limited landscape watering).",
          falseLabel:
            "The use of groundwater to supply water to properties or establishments that are in business to build, supply or sell products, or provide goods, services or repairs and that use water in those processes, or to supply water to the business establishment primarily for employee and customer conveniences (i.e. flushing of toilets, sanitary purposes, or limited landscape watering).",
          nullLabel:
            "The use of groundwater to supply water to properties or establishments that are in business to build, supply or sell products, or provide goods, services or repairs and that use water in those processes, or to supply water to the business establishment primarily for employee and customer conveniences (i.e. flushing of toilets, sanitary purposes, or limited landscape watering).",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_dewatering",
        label: "Dewatering Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A well used temporarily for lowering the water table for construction purposes.",
          falseLabel:
            "A well used temporarily for lowering the water table for construction purposes.",
          nullLabel:
            "A well used temporarily for lowering the water table for construction purposes.",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_industrial",
        label: "Industrial Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "Use of groundwater primarily in the building, production, manufacturing, or alteration of a product or goods, or to wash, cleanse, cool, or heat such goods or products.",
          falseLabel:
            "Use of groundwater primarily in the building, production, manufacturing, or alteration of a product or goods, or to wash, cleanse, cool, or heat such goods or products.",
          nullLabel:
            "Use of groundwater primarily in the building, production, manufacturing, or alteration of a product or goods, or to wash, cleanse, cool, or heat such goods or products.",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_injection",
        label: "Injection Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A well used to inject water or other material into a subsurface formation or into pipe or tubing placed in the formation for the purpose of storage or disposal of the fluid.",
          falseLabel:
            "A well used to inject water or other material into a subsurface formation or into pipe or tubing placed in the formation for the purpose of storage or disposal of the fluid.",
          nullLabel:
            "A well used to inject water or other material into a subsurface formation or into pipe or tubing placed in the formation for the purpose of storage or disposal of the fluid.",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_monitor",
        label: "Monitor Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A well generally used for collecting water-quality and/or water-level data on an ongoing basis.",
          falseLabel:
            "A well generally used for collecting water-quality and/or water-level data on an ongoing basis.",
          nullLabel:
            "A well generally used for collecting water-quality and/or water-level data on an ongoing basis.",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_aquifer_test_obser",
        label: "Aquifer Test Observation Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A well for observing water level changes during an aquifer test, drilled prior to drilling a well in connection with an operating permit to evaluate groundwater quality or quantity, including a test well required under Section 11.6.4.",
          falseLabel:
            "A well for observing water level changes during an aquifer test, drilled prior to drilling a well in connection with an operating permit to evaluate groundwater quality or quantity, including a test well required under Section 11.6.4.",
          nullLabel:
            "A well for observing water level changes during an aquifer test, drilled prior to drilling a well in connection with an operating permit to evaluate groundwater quality or quantity, including a test well required under Section 11.6.4.",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_aquifer_test_pump",
        label: "Aquifer Test Pumping Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "The pumping well used during an aquifer test, drilled prior to drilling a well in connection with an operating permit to evaluate groundwater quality or quantity, including a test well required under Section 11.6.4.",
          falseLabel:
            "The pumping well used during an aquifer test, drilled prior to drilling a well in connection with an operating permit to evaluate groundwater quality or quantity, including a test well required under Section 11.6.4.",
          nullLabel:
            "The pumping well used during an aquifer test, drilled prior to drilling a well in connection with an operating permit to evaluate groundwater quality or quantity, including a test well required under Section 11.6.4.",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "fl_remediation",
        label: "Remediation Well",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel:
            "A well used to pump or vent contaminated air, water, or fluids from the ground.",
          falseLabel:
            "A well used to pump or vent contaminated air, water, or fluids from the ground.",
          nullLabel:
            "A well used to pump or vent contaminated air, water, or fluids from the ground.",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "other_purpose_type",
        type: "subHeader",
        label: "Other Purpose Type",
      },
      {
        name: "fl_other_exempt",
        label: "Other Purpose Type",
        type: "checkbox",
        exclusiveGroup: "purposeOfWell",
        validation: ValidationSchemas.boolean,
        GridItem: { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 },
        optionLabels: {
          trueLabel: "Other Purpose Type",
          falseLabel: "Other Purpose Type",
          nullLabel: "Other Purpose Type",
        },
        infoTooltip:
          "Requires Drilling Permit. Complete this Well Notification Form and then contact the District Office for a Drilling Permit Application.",
      },
      {
        name: "other_desc",
        label: "Other Purpose Description",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) => values.fl_other_exempt,
          deps: ["fl_other_exempt"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (data.fl_other_exempt && !data.other_desc) {
            return "Required if Other Purpose Type is selected";
          }
          return undefined;
        },
      },
    ],
  },
  {
    title: "Well Location",
    step: "Well Location",
    fields: [
      {
        name: "copy_location_address",
        type: "custom",
        render: () => (
          <CopyFieldsButton
            label="Same as Owner Address"
            fieldMap={{
              address_line_1_owner: "address_line_1_location",
              address_city_owner: "address_city_location",
              address_state_owner: "address_state_location",
              address_zip_owner: "address_zip_location",
            }}
          />
        ),
      },
      {
        name: "address_line_1_location",
        label: "Address",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_city_location",
        label: "City",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_state_location",
        label: "State",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_zip_location",
        label: "Zip",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "hays_county_property_id",
        label: "Hays County Property ID",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      { name: "location_map", type: "map", label: "Find Lat/Lon/Elev" },
      {
        name: "latitude",
        label: "Latitude",
        type: "number",
        validation: ValidationSchemas.coordinate.lat,
        infoTooltip:
          "i.e. 30.1234567 (use decimal degrees, not degrees/minutes/seconds)",
      },
      {
        name: "longitude",
        label: "Longitude",
        type: "number",
        validation: ValidationSchemas.coordinate.lon,
        infoTooltip:
          "i.e. -97.1234567 (use decimal degrees, not degrees/minutes/seconds)",
      },
      {
        name: "ft_elevation",
        label: "Elevation (ft)",
        type: "number",
        validation: ValidationSchemas.number,
        infoTooltip:
          "Elevation of the well location above sea level in feet. If unknown, leave blank.",
      },
      {
        name: "address_directions_location",
        label: "Directions to the well location on the property",
        type: "text",
        validation: ValidationSchemas.string,
      },
    ],
  },
  {
    title: "Well Spacing Standards",
    step: "Well Spacing Standards",
    fields: [
      {
        name: "exempt_purpose_types",
        type: "subHeader",
        // label: "Exempt Purpose Types",
        content: (
          <>
            See{" "}
            <a
              href="https://haysgroundwater.com/regulatory/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Hays Trinity Groundwater Conservation District Rules
            </a>{" "}
            for full definitions.
          </>
        ),
      },
      {
        name: "ft_closest_property_line",
        label: "Feet from closest property line",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "ft_existing_well",
        label: "Feet from existing well",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "ft_contaminate",
        label: "Feet from contamination source",
        type: "text",
        validation: ValidationSchemas.requiredString,
        infoTooltip:
          "i.e. livestock, poultry, feedlot, barn, compost pile, etc.",
      },
      {
        name: "ft_waste_water",
        label: "Feet from wastewater system",
        type: "text",
        validation: ValidationSchemas.requiredString,
        infoTooltip:
          "i.e. septic system, aerobic system, spray field, etc. (not including septic tank)",
      },
      {
        name: "ft_septic_tank",
        label: "Feet from septic tank",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "fl_meets_spacing_req",
        label:
          "I acknowledge the spacing requirements. If those requirements cannot be met, I have contacted the district directly and obtained approval to proceed with this form.",
        type: "radioBoolean",
        validation: ValidationSchemas.requiredTrueBoolean,
      },
      {
        name: "fl_within_50_ft_pws",
        label: "Within 50 ft of Public Water System?",
        type: "radioBoolean",
        validation: ValidationSchemas.requiredBoolean,
      },
      {
        name: "pws",
        label: "Public Water System (if applicable)",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) => values.fl_within_50_ft_pws,
          deps: ["fl_within_50_ft_pws"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (data.fl_within_50_ft_pws && !data.pws) {
            return "Required if within 50 ft of Public Water System";
          }
          return undefined;
        },
      },
      {
        name: "within_mngt_zone_virtual_boolean",
        label: "Within Groundwater Management Zone?",
        type: "radioBoolean",
        optionLabels: {
          falseLabel: "No, Regular District Outside any GMZ",
        },
        validation: ValidationSchemas.requiredBoolean,
      },
      {
        name: "zone_ndx",
        label: "Groundwater Management Zone",
        type: "selectSingle",
        lookupTable: lookupConfig.groundwaterManagementZones,
        validation: ValidationSchemas.positiveInt.nullable().optional(),
        showIf: {
          // NOTE: This is a virtual field used for conditional visibility only, not part of the backend schema
          fn: (values) => values.within_mngt_zone_virtual_boolean,
          deps: ["within_mngt_zone_virtual_boolean", "zone_ndx"],
          resetValue: null,
          required: true,
        },
        refine: (data) => {
          // NOTE: This is a virtual field used for conditional visibility only, not part of the backend schema
          if (data.within_mngt_zone_virtual_boolean && !data.zone_ndx) {
            return "Required if Within Groundwater Management Zone is selected";
          }
          return undefined;
        },
      },
    ],
  },
  {
    title: "Well Driller's Information",
    step: "Well Driller's Info",
    fields: [
      {
        name: "id_contact_driller",
        label: "Company Contracted",
        type: "selectSingle",
        lookupTable: lookupConfig.drillersList,
        validation: ValidationSchemas.positiveInt.nullable().optional(),
        infoTooltip:
          "If no driller is selected, you must provide the driller's information below.",
      },
      {
        name: "unlisted_driller",
        type: "subHeader",
        label: "Unlisted Driller",
        content:
          "If driller is not in list, please complete the following information.",
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_driller
            ),
          deps: ["id_contact_driller"],
        },
      },
      {
        name: "name_company_driller",
        label: "Company",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_driller
            ),
          deps: ["id_contact_driller"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_driller
            ) &&
            !data.name_company_driller
          ) {
            return "Required if no driller is selected";
          }
          return undefined;
        },
      },
      {
        name: "address_line_1_driller",
        label: "Address",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_driller
            ),
          deps: ["id_contact_driller"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_driller
            ) &&
            !data.address_line_1_driller
          ) {
            return "Required if no driller is selected";
          }
          return undefined;
        },
      },
      {
        name: "address_city_driller",
        label: "City",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_driller
            ),
          deps: ["id_contact_driller"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_driller
            ) &&
            !data.address_city_driller
          ) {
            return "Required if no driller is selected";
          }
          return undefined;
        },
      },
      {
        name: "address_state_driller",
        label: "State",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_driller
            ),
          deps: ["id_contact_driller"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_driller
            ) &&
            !data.address_state_driller
          ) {
            return "Required if no driller is selected";
          }
          return undefined;
        },
      },
      {
        name: "address_zip_driller",
        label: "Zip",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_driller
            ),
          deps: ["id_contact_driller"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_driller
            ) &&
            !data.address_zip_driller
          ) {
            return "Required if no driller is selected";
          }
          return undefined;
        },
      },
      {
        name: "num_license_driller",
        label: "License Number",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_driller
            ),
          deps: ["id_contact_driller"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_driller
            ) &&
            !data.num_license_driller
          ) {
            return "Required if no driller is selected";
          }
          return undefined;
        },
      },
      {
        name: "phone_primary_driller",
        label: "Phone Number",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_driller
            ),
          deps: ["id_contact_driller"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_driller
            ) &&
            !data.phone_primary_driller
          ) {
            return "Required if no driller is selected";
          }
          return undefined;
        },
      },
      {
        name: "phone_fax_driller",
        label: "Fax Number",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_driller
            ),
          deps: ["id_contact_driller"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_driller
            ) &&
            !data.phone_fax_driller
          ) {
            return "Required if no driller is selected";
          }
          return undefined;
        },
      },
      {
        name: "email_driller",
        label: "Email",
        type: "text",
        validation: ValidationSchemas.email,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_driller
            ),
          deps: ["id_contact_driller"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_driller
            ) &&
            !data.email_driller
          ) {
            return "Required if no driller is selected";
          }
          return undefined;
        },
      },
    ],
  },
  {
    title: "Pump Installer's Information",
    step: "Pump Installer's Info",
    fields: [
      {
        name: "copy_driller_id",
        type: "custom",
        render: () => (
          <CopyFieldsButton
            label="Same as Driller"
            fieldMap={{
              id_contact_driller: "id_contact_installer",
            }}
          />
        ),
        showIf: {
          fn: (values) => !!values.id_contact_driller,
          deps: ["id_contact_driller"],
        },
      },
      {
        name: "id_contact_installer",
        label: "Company Contracted",
        type: "selectSingle",
        lookupTable: lookupConfig.installersList,
        validation: ValidationSchemas.positiveInt.nullable().optional(),
        infoTooltip:
          "If no installer is selected, you must provide the installer's information below.",
      },
      {
        name: "unlisted_installer",
        type: "subHeader",
        label: "Unlisted Installer",
        content:
          "If installer is not in list, please complete the following information.",
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_installer
            ),
          deps: ["id_contact_installer"],
        },
      },
      {
        name: "copy_driller_address",
        type: "custom",
        render: () => (
          <CopyFieldsButton
            label="Same as Driller"
            fieldMap={{
              name_company_driller: "name_company_installer",
              address_line_1_driller: "address_line_1_installer",
              address_city_driller: "address_city_installer",
              address_state_driller: "address_state_installer",
              address_zip_driller: "address_zip_installer",
              num_license_driller: "num_license_installer",
              phone_primary_driller: "phone_primary_installer",
              phone_fax_driller: "phone_fax_installer",
              email_driller: "email_installer",
            }}
          />
        ),
        showIf: {
          fn: (values) => !values.id_contact_driller,
          deps: ["id_contact_driller"],
        },
      },
      {
        name: "name_company_installer",
        label: "Company",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_installer
            ),
          deps: ["id_contact_installer"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_installer
            ) &&
            !data.name_company_installer
          ) {
            return "Required if no installer is selected";
          }
          return undefined;
        },
      },
      {
        name: "address_line_1_installer",
        label: "Address",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_installer
            ),
          deps: ["id_contact_installer"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_installer
            ) &&
            !data.address_line_1_installer
          ) {
            return "Required if no installer is selected";
          }
          return undefined;
        },
      },
      {
        name: "address_city_installer",
        label: "City",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_installer
            ),
          deps: ["id_contact_installer"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_installer
            ) &&
            !data.address_city_installer
          ) {
            return "Required if no installer is selected";
          }
          return undefined;
        },
      },
      {
        name: "address_state_installer",
        label: "State",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_installer
            ),
          deps: ["id_contact_installer"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_installer
            ) &&
            !data.address_state_installer
          ) {
            return "Required if no installer is selected";
          }
          return undefined;
        },
      },
      {
        name: "address_zip_installer",
        label: "Zip",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_installer
            ),
          deps: ["id_contact_installer"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_installer
            ) &&
            !data.address_zip_installer
          ) {
            return "Required if no installer is selected";
          }
          return undefined;
        },
      },
      {
        name: "num_license_installer",
        label: "License Number",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_installer
            ),
          deps: ["id_contact_installer"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_installer
            ) &&
            !data.num_license_installer
          ) {
            return "Required if no installer is selected";
          }
          return undefined;
        },
      },
      {
        name: "phone_primary_installer",
        label: "Phone Number",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_installer
            ),
          deps: ["id_contact_installer"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_installer
            ) &&
            !data.phone_primary_installer
          ) {
            return "Required if no installer is selected";
          }
          return undefined;
        },
      },
      {
        name: "phone_fax_installer",
        label: "Fax Number",
        type: "text",
        validation: ValidationSchemas.string,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_installer
            ),
          deps: ["id_contact_installer"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_installer
            ) &&
            !data.phone_fax_installer
          ) {
            return "Required if no installer is selected";
          }
          return undefined;
        },
      },
      {
        name: "email_installer",
        label: "Email",
        type: "text",
        validation: ValidationSchemas.email,
        showIf: {
          fn: (values) =>
            [null, undefined, 99999999, "99999999"].includes(
              values.id_contact_installer
            ),
          deps: ["id_contact_installer"],
          resetValue: "",
          required: true,
        },
        refine: (data) => {
          if (
            [null, undefined, 99999999, "99999999"].includes(
              data.id_contact_installer
            ) &&
            !data.email_installer
          ) {
            return "Required if no installer is selected";
          }
          return undefined;
        },
      },
    ],
  },
  {
    title: "General Well Information",
    subtitle: "If this is a new well, please provide an estimate.",
    step: "General Well Info",
    fields: [
      {
        name: "size_borehole",
        label: "Borehole Diameter (inches)",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "size_casing",
        label: "Casing Size (inches)",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "depth_well",
        label: "Well Depth (feet)",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "size_pump",
        label: "Pump Size (hp)",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "cap_production",
        label: "Production Capability (gpm)",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "dt_start_drill",
        label: "Desired Start Date of Drilling/Alteration",
        type: "date",
        validation: ValidationSchemas.date,
      },
      {
        name: "loc_water_use",
        label: "Where will water from the well be used?",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
    ],
  },
  {
    title: "Fee",
    step: "Fee",
    fields: [
      {
        name: "new_construction_fee",
        type: "subHeader",
        label: "New Well Construction Fee: $1000",
        content:
          "Wells used solely for agricultural & livestock purposes are exempt from fees provided a copy of the 'Texas Agricultural or Timber Registration Number' is submitted to the district.",
        showIf: {
          fn: (values) =>
            !values.fl_agricultural && !values.fl_geothermal_exempt,
          deps: ["fl_agricultural", "fl_geothermal_exempt"],
        },
      },
      {
        name: "geothermal_construction_fee",
        type: "subHeader",
        label: "GeoExchange / Geothermal Construction Fees",
        content: (
          <span>
            $300 for the first five wells, $60 for each additional well
          </span>
        ),
        showIf: {
          fn: (values) =>
            values.fl_geothermal_exempt &&
            !values.num_of_wells_geothermal_exempt,
          deps: ["fl_geothermal_exempt", "num_of_wells_geothermal_exempt"],
        },
      },
      {
        name: "geothermal_construction_fee_calculated",
        type: "subHeader",
        label: (values) => {
          const numWells = values.num_of_wells_geothermal_exempt || 0;
          let fee = 0;
          if (numWells > 0) {
            fee = numWells <= 5 ? 300 : 300 + (numWells - 5) * 60;
          }
          return `GeoExchange / Geothermal Construction Fee: $${fee}`;
        },
        content: (values) => {
          const numWells = values.num_of_wells_geothermal_exempt || 0;
          if (numWells <= 5 && numWells > 0) {
            return `${numWells} well${
              numWells > 1 ? "s" : ""
            } = $300 (flat rate for up to 5 wells)`;
          } else if (numWells > 5) {
            const additionalWells = numWells - 5;
            const additionalFee = additionalWells * 60;
            return `5 wells ($300) + ${additionalWells} additional well${
              additionalWells > 1 ? "s" : ""
            } ($${additionalFee}) = $${300 + additionalFee}`;
          }
          return null;
        },
        showIf: {
          fn: (values) =>
            values.fl_geothermal_exempt &&
            values.num_of_wells_geothermal_exempt > 0,
          deps: ["fl_geothermal_exempt", "num_of_wells_geothermal_exempt"],
        },
      },
      {
        name: "new_construction_fee_waived",
        type: "subHeader",
        label: "Fee Waived",
        content:
          "By providing your Texas Agricultural or Timber Registration number, you confirm eligibility for the agricultural exemption and the $1,000 fee will be waived.",
        showIf: {
          fn: (values) => values.fl_agricultural,
          deps: ["fl_agricultural"],
        },
      },
      {
        name: "payment_meth_ndx",
        label: "Select Payment Method",
        type: "selectSingle",
        lookupTable: lookupConfig.paymentMethods,
        validation: ValidationSchemas.positiveInt.nullable().optional(),
        defaultValue: null,
        showIf: {
          fn: (values) => !values.fl_agricultural,
          deps: ["fl_agricultural"],
          required: true,
        },
        refine: (data) => {
          if (!data.fl_agricultural && !data.payment_meth_ndx) {
            return "Expected number, received null";
          }
          return undefined;
        },
      },
      {
        name: "fl_fee_agree",
        label:
          "I understand that if my check is returned for NSF, I am responsible for the NSF fee charged by the bank.",
        type: "radioBoolean",
        validation: ValidationSchemas.requiredBoolean.nullable().optional(),
        defaultValue: null,
        showIf: {
          fn: (values) => !values.fl_agricultural,
          deps: ["fl_agricultural"],
          required: true,
        },
        refine: (data) => {
          if (!data.fl_agricultural && data.fl_fee_agree !== true) {
            return "Invalid literal value, expected true";
          }
          return undefined;
        },
      },
    ],
  },
  {
    title: "Prepared By",
    subtitle:
      "By signing this form, I declare that I have knowledge of the information provided in this form and that it is true and correct, to the best of my knowledge and belief. Furthermore, I am familiar with all pertinent requirements contained in the Hays Trinity Groundwater Conservation District Rules including the District's well construction standards. I understand that within 60 days after the completion of the well, the driller must submit the State of Texas Well Report (Well Log) to the District.",
    step: "Prepared By",
    fields: [
      {
        name: "agrees_to_conditions",
        label:
          "I agree that my typed signature is binding and indicates my acceptance of the above statement.",
        type: "checkbox",
        optionLabels: {
          falseLabel: "",
        },
        validation: ValidationSchemas.requiredTrueBoolean,
      },
      {
        name: "contact_info",
        type: "subHeader",
        label: "Additional Contact Information (if different from Well Owner)",
      },
      {
        name: "copy_owner_info",
        type: "custom",
        render: () => (
          <CopyFieldsButton
            label="Same as Well Owner Information"
            fieldMap={{
              name_first_owner: "name_first_preparer",
              name_last_owner: "name_last_preparer",
              address_line_1_owner: "address_line_1_preparer",
              address_city_owner: "address_city_preparer",
              address_state_owner: "address_state_preparer",
              address_zip_owner: "address_zip_preparer",
              phone_primary_owner: "phone_primary_preparer",
            }}
          />
        ),
      },
      {
        name: "name_first_preparer",
        label: "First Name",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "name_last_preparer",
        label: "Last Name",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_line_1_preparer",
        label: "Address",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_city_preparer",
        label: "City",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_state_preparer",
        label: "State",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "address_zip_preparer",
        label: "Zip",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
      {
        name: "phone_primary_preparer",
        label: "Phone Number",
        type: "text",
        validation: ValidationSchemas.requiredString,
      },
    ],
  },
  {
    title: "Comments Regarding the Well",
    step: "Comments",
    fields: [
      {
        name: "comment",
        label: "Comments",
        type: "text",
        validation: ValidationSchemas.string,
      },
    ],
  },
];

export const adminWellRegistrationFormConfig = [
  {
    title: "Administration",
    subtitle:
      "Internal fields for staff to review, track, and approve pending registrations. These are never shown on the public form.",
    step: "Admin",
    adminOnly: true,
    fields: [
      /* ─────────────────────────  RECORD META  ───────────────────────── */
      {
        name: "subHeader_record_metadata",
        type: "subHeader",
        label: "Record Metadata",
      },
      {
        name: "pending_well_reg_ndx",
        label: "Record ID",
        type: "text",
        readOnly: true,
      },
      {
        name: "dt_received",
        label: "Date Received",
        type: "dateTime",
        infoTooltip: "Date the user submitted the registration to the District",
        readOnly: true,
      },
      {
        name: "created_by_user",
        label: "Created By",
        type: "text",
        readOnly: true,
      },
      {
        name: "created_dt_gmt",
        label: "Date Created",
        type: "dateTime",
        readOnly: true,
      },
      {
        name: "updated_by_user",
        label: "Last Updated By",
        type: "text",
        readOnly: true,
      },
      {
        name: "updated_dt_gmt",
        label: "Date Last Updated",
        type: "dateTime",
        readOnly: true,
      },

      /* ────────────────────  REVIEW / APPROVAL STATUS  ────────────────── */
      {
        name: "subHeader_review_approval_status",
        type: "subHeader",
        label: "Review & Approval Status",
      },
      {
        name: "well_approved",
        label: "Approval Status",
        type: "radioBoolean",
        defaultValue: null,
        optionLabels: {
          trueLabel: "Approved",
          falseLabel: "Denied",
          nullLabel: "Pending",
        },
        infoTooltip:
          "This is the final approval for the well registration. Selecting and saving this will finalize the record.",
        validation: ValidationSchemas.boolean.nullable().optional(),
      },
      {
        name: "dt_approved",
        label: "Date Approved",
        type: "dateTime",
        readOnly: true,
      },
      {
        name: "approved_by_user",
        label: "Approved By",
        type: "text",
        readOnly: true,
      },
      {
        name: "dt_denied",
        label: "Date Denied",
        type: "dateTime",
        readOnly: true,
      },
      {
        name: "denied_by_user",
        label: "Denied By",
        type: "text",
        readOnly: true,
      },
      {
        name: "reg_abandoned",
        label: "Abandon Registration",
        type: "radioBoolean",
        defaultValue: null,
        optionLabels: {
          trueLabel: "Abandon",
          falseLabel: "Active",
        },
        infoTooltip:
          "If the registration is abandoned, it will be marked as such but not deleted. This is for records that were started but never completed.",
        validation: ValidationSchemas.boolean.nullable().optional(),
      },

      /* ─────────────────────────  PAYMENT / FEE  ──────────────────────── */
      {
        name: "subHeader_payment_fee",
        type: "subHeader",
        label: "Payment / Accounting",
      },
      //TODO need to add payment status lookup table
      {
        name: "payment_status",
        label: "Payment Status",
        type: "selectSingle",
        lookupTable: lookupConfig.paymentStatuses,
        validation: ValidationSchemas.string.nullable().optional(),
      },
      {
        name: "amt_paid",
        label: "Amount Paid ($)",
        type: "number",
        validation: ValidationSchemas.number,
      },
      {
        name: "payment_meth_ndx",
        label: "Select Payment Method",
        type: "selectSingle",
        lookupTable: lookupConfig.paymentMethods,
        validation: ValidationSchemas.positiveInt.nullable().optional(),
        defaultValue: null,
      },
      {
        name: "payment_method",
        label: "Payment Method (Description)",
        type: "text",
      },
      {
        name: "check_number",
        label: "Check Number",
        type: "text",
      },
      {
        name: "stripe_reference_number",
        label: "Stripe Reference #",
        type: "text",
      },

      /* ─────────────────────  WELL / TRACKING ID FIELDS  ───────────────── */
      {
        name: "subHeader_well_tracking_ids",
        type: "subHeader",
        label: "Well & Tracking IDs",
      },
      {
        name: "id_well",
        label: "District Well ID",
        type: "number",
        validation: ValidationSchemas.positiveInt.nullable().optional(),
      },
      {
        name: "id_well_twdb",
        label: "TWDB Well ID",
        type: "text",
      },
      {
        name: "id_well_report_tracking_number",
        label: "Well Report Tracking Number SDR",
        type: "text",
      },
      {
        name: "grid_number",
        label: "Grid Number",
        type: "text",
      },
      {
        name: "tax_parcel",
        label: "Tax Parcel ID",
        type: "text",
      },

      /* ──────────────────────  ZONE / COMPLIANCE  ─────────────────────── */
      {
        name: "subHeader_zone_compliance",
        type: "subHeader",
        label: "Zone / Compliance",
      },
      {
        name: "within_hgw_mngt_zone",
        label: "Hydro-Geo Mgmt Zone",
        type: "text",
      },

      /* ─────────────────────────  DOCUMENT LINKS  ─────────────────────── */
      {
        name: "subHeader_document_links",
        type: "subHeader",
        label: "Attached Documents",
      },
      {
        name: "doc_well_registration",
        label: "Well Registration File",
      },
      {
        name: "doc_plat",
        label: "Plat Map",
      },
      {
        name: "doc_state_well_report",
        label: "State Well Number GWDB",
      },
      {
        name: "doc_confirmation",
        label: "Confirmation Letter",
      },
      {
        name: "doc_other",
        label: "Other Attachment",
      },

      /* ─────────────────────  INTERNAL NOTES / MISC  ───────────────────── */
      {
        name: "subHeader_internal_notes",
        type: "subHeader",
        label: "Internal Notes",
      },
      {
        name: "alter_desc",
        label: "Alteration Description",
        type: "text",
      },
      {
        name: "prepared_through",
        label: "Prepared Through",
        type: "text",
      },
    ],
  },
];
